import React, { useEffect, useState, useMemo, useRef } from 'react';
import Lenis from 'lenis';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Hero from './components/Hero';
import MenuGrid from './components/MenuGrid';
import TickerMarquee from './components/TickerMarquee';
import HandStory from './components/HandStory';
import GustoRotator from './components/GustoRotator';
import CheckoutOverlay from './components/CheckoutOverlay';
import { PIZZAS, PizzaProductExtended } from './constants';
import { CartItem, PastOrder } from './types';

gsap.registerPlugin(ScrollTrigger);

const AVAILABLE_CRUSTS = [
  '72-Hour Standard Sourdough',
  'Crispy Thin Crust',
  'Garlic Butter Stuffed Crust (+$1.50)',
  'Gluten-Free Sourdough (+$2.00)'
];

const AVAILABLE_TOPPINGS = [
  { name: 'Extra Fresh Burrata', price: 2.0 },
  { name: 'Hot Honey Drizzle', price: 1.5 },
  { name: 'Aged Parmesan', price: 1.0 },
  { name: 'Roasted Garlic', price: 1.0 },
  { name: 'Fresh Basil', price: 1.0 },
  { name: 'Crispy Smoked Bacon', price: 2.0 }
];

const App: React.FC = () => {
  const [scrolled, setScrolled] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [orderStatus, setOrderStatus] = useState<'idle' | 'success' | 'minimized'>('idle');
  
  // Customization & Order History State
  const [orderHistory, setOrderHistory] = useState<PastOrder[]>([]);
  const [openCustomizingId, setOpenCustomizingId] = useState<string | null>(null);
  const [isOrderHistoryOpen, setIsOrderHistoryOpen] = useState(false);
  const cartItemsRef = useRef<HTMLDivElement>(null);
  
  // PWA Install Prompt State
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const installBannerRef = useRef<HTMLDivElement>(null);

  // Persist order details for the receipt
  const [orderedItems, setOrderedItems] = useState<CartItem[]>([]);
  const [orderedTotal, setOrderedTotal] = useState(0);
  const [orderTime, setOrderTime] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [customerName, setCustomerName] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [persistedPaymentMethod, setPersistedPaymentMethod] = useState<'cash' | 'digital'>('cash');
  
  // Notes state
  const [orderNotes, setOrderNotes] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");
  const [persistedOrderNotes, setPersistedOrderNotes] = useState("");
  const [persistedDeliveryNotes, setPersistedDeliveryNotes] = useState("");

  // Load Order History from localStorage
  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem('gusto_order_history');
      if (savedHistory) {
        setOrderHistory(JSON.parse(savedHistory));
      }
    } catch (err) {
      console.error("Failed to load order history:", err);
    }
  }, []);

  // Notification simulation logic
  const sendGustoNotification = (title: string, body: string) => {
    if (!("Notification" in window)) return;
    
    if (Notification.permission === "granted") {
      navigator.serviceWorker.ready.then((registration) => {
        // Fix: Cast the notification options object to any because 'vibrate' may not be present 
        // in standard NotificationOptions definitions depending on the TypeScript configuration.
        registration.showNotification(title, {
          body: body,
          icon: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><circle cx="50" cy="50" r="45" fill="%231A3C28"/><path d="M50,50 L50,10 L75,25 Z" fill="%23E05A38"/></svg>',
          badge: 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><path d="M50,50 L50,10 L75,25 Z" fill="%23E05A38"/></svg>',
          vibrate: [200, 100, 200],
          tag: 'gusto-order-status'
        } as any);
      });
    }
  };

  useEffect(() => {
    setIsInitialized(true);

    // Detect iOS and Standalone Mode
    const iosCheck = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone;
    setIsIOS(iosCheck);

    // Listen for Chrome/Android Install Prompt
    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!isStandalone) {
        setTimeout(() => setShowInstallBanner(true), 3000); // Show after 3s
      }
    };

    // Show iOS Instructions if not standalone
    if (iosCheck && !isStandalone) {
      setTimeout(() => setShowInstallBanner(true), 3000);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Check for "print" query parameter (Stateless Order View)
    const urlParams = new URLSearchParams(window.location.search);
    const orderDataEncoded = urlParams.get('order');
    if (orderDataEncoded) {
      try {
        const decoded = JSON.parse(atob(orderDataEncoded));
        const items = decoded.items.map((i: { id: string, qty: number }) => {
          const pizza = PIZZAS.find(p => p.id === i.id);
          return pizza ? { ...pizza, quantity: i.qty } : null;
        }).filter(Boolean) as CartItem[];

        if (items.length > 0) {
          setOrderedItems(items);
          setOrderedTotal(decoded.total);
          setOrderTime(decoded.time || Date.now());
          setCustomerName(decoded.customer || "Valued Guest");
          setCustomerAddress(decoded.address || "");
          setCustomerPhone(decoded.phone || "");
          setPersistedOrderNotes(decoded.orderNotes || "");
          setPersistedDeliveryNotes(decoded.deliveryNotes || "");
          setPersistedPaymentMethod(decoded.paymentMethod || "cash");
          setOrderStatus('success');
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      } catch (err) {
        console.error("Failed to parse order data from URL:", err);
      }
    }

    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    lenis.on('scroll', (e: any) => {
      setScrolled(e.scroll > 50);
      ScrollTrigger.update();
    });

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        ScrollTrigger.refresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    ScrollTrigger.refresh();

    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);

    return () => {
      lenis.destroy();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      ScrollTrigger.getAll().forEach(t => t.kill());
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    if (showInstallBanner && installBannerRef.current) {
      gsap.fromTo(installBannerRef.current, 
        { y: 100, opacity: 0 }, 
        { y: 0, opacity: 1, duration: 1, ease: "expo.out" }
      );
    }
  }, [showInstallBanner]);

  const handleInstallClick = () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      deferredPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('User accepted the install prompt');
        }
        setDeferredPrompt(null);
        setShowInstallBanner(false);
      });
    }
  };

  // GSAP animation for cart item changes
  useEffect(() => {
    if (isCartOpen && cart.length > 0 && cartItemsRef.current) {
      const items = cartItemsRef.current.querySelectorAll('.cart-item-anim');
      if (items.length > 0) {
        gsap.fromTo(
          items,
          { x: 25, opacity: 0, scale: 0.96 },
          { x: 0, opacity: 1, scale: 1, duration: 0.35, stagger: 0.06, ease: "power2.out", overwrite: "auto" }
        );
      }
    }
  }, [isCartOpen, cart.length]);

  const getItemUnitPrice = (item: CartItem): number => {
    let base = parseInt(item.price.replace('$', '')) || 0;
    if (item.customization?.crust) {
      if (item.customization.crust.includes('+$1.50')) base += 1.5;
      if (item.customization.crust.includes('+$2.00') || item.customization.crust.includes('+$2')) base += 2;
    }
    if (item.customization?.extraToppings) {
      item.customization.extraToppings.forEach(t => {
        base += t.price;
      });
    }
    return base;
  };

  const totalPrice = useMemo(() => {
    return cart.reduce((acc, item) => acc + (getItemUnitPrice(item) * item.quantity), 0);
  }, [cart]);

  // Lock background scrolling when cart or checkout is open
  useEffect(() => {
    if (isCartOpen || isCheckoutOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isCartOpen, isCheckoutOpen]);

  const addToCart = (pizza: PizzaProductExtended) => {
    setCart(prev => {
      // Auto-open cart drawer ONLY on the very first item added to an empty cart
      if (prev.length === 0) {
        setIsCartOpen(true);
      }
      const existing = prev.find(item => item.id === pizza.id);
      if (existing) {
        return prev.map(item => 
          item.id === pizza.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...pizza, quantity: 1, customization: { crust: '72-Hour Standard Sourdough', extraToppings: [] } }];
    });
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(0, item.quantity + delta);
        return { ...item, quantity: newQty };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const setItemCrust = (itemId: string, crust: string) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          customization: {
            ...item.customization,
            crust
          }
        };
      }
      return item;
    }));
  };

  const toggleItemTopping = (itemId: string, topping: { name: string; price: number }) => {
    setCart(prev => prev.map(item => {
      if (item.id === itemId) {
        const currentToppings = item.customization?.extraToppings || [];
        const exists = currentToppings.some(t => t.name === topping.name);
        const updatedToppings = exists
          ? currentToppings.filter(t => t.name !== topping.name)
          : [...currentToppings, topping];
        return {
          ...item,
          customization: {
            ...item.customization,
            extraToppings: updatedToppings
          }
        };
      }
      return item;
    }));
  };

  const requestNotificationPermission = async () => {
    if ("Notification" in window) {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        return true;
      }
    }
    return false;
  };

  const onOrderSuccess = async (customerData?: { name: string, address: string, phone: string, orderNotes: string, deliveryNotes: string, paymentMethod: 'cash' | 'digital' }) => {
    const finalCartItems = [...cart];
    const finalTotal = totalPrice;

    setOrderedItems(finalCartItems);
    setOrderedTotal(finalTotal);
    setOrderTime(Date.now());

    // Save order into Order History in localStorage
    const newPastOrder: PastOrder = {
      id: `GST-${Math.floor(100000 + Math.random() * 900000)}`,
      date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
      timestamp: Date.now(),
      items: finalCartItems,
      total: finalTotal,
      customerName: customerData?.name || customerName || 'Valued Guest',
      paymentMethod: customerData?.paymentMethod || 'cash'
    };

    setOrderHistory(prev => {
      const updated = [newPastOrder, ...prev];
      try {
        localStorage.setItem('gusto_order_history', JSON.stringify(updated));
      } catch (e) {
        console.error("Failed to save order history:", e);
      }
      return updated;
    });

    if (customerData) {
      setCustomerName(customerData.name);
      setCustomerAddress(customerData.address);
      setCustomerPhone(customerData.phone);
      setPersistedOrderNotes(customerData.orderNotes);
      setPersistedDeliveryNotes(customerData.deliveryNotes);
      setPersistedPaymentMethod(customerData.paymentMethod);
    }
    setOrderStatus('success');
    setIsCheckoutOpen(false);
    setCart([]);
    setOrderNotes("");
    setDeliveryNotes("");

    // Start Notification Simulation
    const permissionGranted = await requestNotificationPermission();
    if (permissionGranted) {
      // 0s: Confirmed
      sendGustoNotification("GUSTO: Order Confirmed", "Your order is confirmed and being prepared.");
      
      // 2m: Cooking (Simulated)
      setTimeout(() => {
        sendGustoNotification("GUSTO: In the oven", "Your order is being baked in our stone hearth.");
      }, 120000);

      // 8m: Delivery (Simulated)
      setTimeout(() => {
        sendGustoNotification("GUSTO: Out for delivery", "Your order is on its way to you.");
      }, 480000);

      // 10m: Delivered (Simulated)
      setTimeout(() => {
        sendGustoNotification("GUSTO: Delivered", "Your order has arrived. Enjoy.");
      }, 600000);
    }
  };

  const handleShare = async () => {
    const orderData = {
      items: orderedItems.map(i => ({ id: i.id, qty: i.quantity })),
      total: orderedTotal,
      customer: customerName,
      address: customerAddress,
      phone: customerPhone,
      orderNotes: persistedOrderNotes,
      deliveryNotes: persistedDeliveryNotes,
      paymentMethod: persistedPaymentMethod,
      time: orderTime
    };
    const shareUrl = `${window.location.origin}${window.location.pathname}?order=${btoa(JSON.stringify(orderData))}`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: 'GUSTO Receipt',
          text: `Receipt for order #${orderTime ? orderTime.toString().slice(-6) : 'GUSTO'}`,
          url: shareUrl,
        });
      } catch (err) {
        console.log('User cancelled share or error occurred', err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      } catch (err) {
        console.error('Failed to copy link', err);
      }
    }
  };

  const totalItems = cart.reduce((acc, item) => acc + item.quantity, 0);

  const elapsedMinutes = orderTime ? (currentTime - orderTime) / 60000 : 0;
  const isDelivering = elapsedMinutes >= 2;
  const isDelivered = elapsedMinutes >= 10;
  const progressPercent = Math.min(100, (elapsedMinutes / 10) * 100);

  const bannerShift = "translate-y-[50px] md:translate-y-[60px]";

  // Format date and time for thermal receipt
  const dateStr = orderTime ? new Date(orderTime).toLocaleDateString() : '01-01-2025';
  const timeStr = orderTime ? new Date(orderTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '10:35';

  return (
    <div className="relative w-full overflow-x-hidden">
      {orderStatus !== 'idle' && (
        <div 
          id="order-success-overlay"
          className={`fixed left-0 w-full z-[1000] transition-all duration-700 ease-expo-out overflow-y-auto scrollbar-hide overscroll-behavior-contain ${
            orderStatus === 'success' 
            ? 'inset-0 bg-[#F2E9D8] flex flex-col' 
            : 'top-0 bg-[#1A3C28] border-b border-[#E05A38]/30 py-2.5 px-4 md:px-8 flex flex-row items-center justify-between shadow-xl'
          }`}
          data-lenis-prevent={orderStatus === 'success' ? true : undefined}
          style={orderStatus === 'success' ? { height: '100dvh' } : {}}
        >
           {orderStatus === 'success' ? (
             <div id="receipt-printable" className="max-w-4xl mx-auto w-full flex flex-col items-center animate-in fade-in zoom-in duration-700 p-4 md:p-8 lg:p-10 relative">
               
               {/* --- THERMAL RECEIPT (Print Only) --- */}
               <div className="thermal-receipt">
                 <h1 className="script-font">Receipt</h1>
                 {/* High-Contrast Print Logo */}
                 <svg viewBox="0 0 100 100" className="print-logo">
                    {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                      <g key={i} transform={`rotate(${angle} 50 50)`}>
                        <path d="M50,50 L50,10 L75,25 Z" fill="black" />
                      </g>
                    ))}
                    <circle cx="50" cy="50" r="8" fill="white" stroke="black" strokeWidth="2" />
                    <circle cx="50" cy="50" r="4" fill="black" />
                 </svg>

                 <p className="bold uppercase tracking-widest">Gusto Studio</p>
                 <p>102 S Bronough St, Tallahassee</p>
                 <p>Tel: (850) GUSTO-STUDIO</p>
                 
                 <div className="divider"></div>
                 <div className="flex-row">
                   <span>Date: {dateStr}</span>
                   <span>{timeStr}</span>
                 </div>
                 <div className="divider"></div>
                 
                 {orderedItems.map((item, idx) => {
                   const unitPrice = getItemUnitPrice(item);
                   return (
                     <div key={idx} style={{ marginBottom: "6px" }}>
                       <div className="flex-row">
                         <div>
                           <span>{item.quantity}x {item.name}</span>
                           {item.customization?.crust && item.customization.crust !== "72-Hour Standard Sourdough" && (
                             <p style={{ fontSize: "8px", opacity: 0.8 }}>• {item.customization.crust.split("(")[0].trim()}</p>
                           )}
                           {item.customization?.extraToppings && item.customization.extraToppings.length > 0 && (
                             <p style={{ fontSize: "8px", opacity: 0.8 }}>• +{item.customization.extraToppings.map(t => t.name).join(", ")}</p>
                           )}
                         </div>
                         <span>${(unitPrice * item.quantity).toFixed(2)}</span>
                       </div>
                     </div>
                   );
                 })}
                 
                 <div className="divider"></div>
                 <div className="flex-row bold">
                   <span>{persistedPaymentMethod === 'digital' ? 'TOTAL PAID' : 'AMOUNT DUE'}</span>
                   <span>${orderedTotal}.00</span>
                 </div>
                 <div className="flex-row">
                   <span>Sub-total</span>
                   <span>${orderedTotal}.00</span>
                 </div>
                 <div className="flex-row">
                   <span>Sales Tax (0%)</span>
                   <span>$0.00</span>
                 </div>
                 <div className="flex-row">
                   <span>Balance</span>
                   <span>{persistedPaymentMethod === 'digital' ? '$0.00' : `$${orderedTotal}.00`}</span>
                 </div>
                 
                 <div className="divider"></div>
                 <div style={{textAlign: 'left', marginTop: '10px'}}>
                   <p className="bold uppercase tracking-widest" style={{fontSize: '9px', marginBottom: '10px'}}>Delivery To:</p>
                   <p className="bold">{customerName || "Valued Guest"}</p>
                   {customerAddress && <p>{customerAddress}</p>}
                   {customerPhone && <p>Contact: {customerPhone}</p>}
                 </div>

                 {(persistedOrderNotes || persistedDeliveryNotes) && (
                   <>
                     <div className="divider"></div>
                     <div style={{textAlign: 'left'}}>
                        <p className="bold uppercase tracking-widest" style={{fontSize: '9px', marginBottom: '10px'}}>Notes:</p>
                        {persistedOrderNotes && <p style={{fontSize: '10px'}}><span className="bold">Order:</span> {persistedOrderNotes}</p>}
                        {persistedDeliveryNotes && <p style={{fontSize: '10px'}}><span className="bold">Delivery:</span> {persistedDeliveryNotes}</p>}
                     </div>
                   </>
                 )}

                 <div className="qr-container">
                    {/* Official Scannable Barcode Link */}
                    <img 
                      src="https://barcode.tec-it.com/barcode.ashx?data=https%3A%2F%2Fsaadmughal-gusto.vercel.app%2F&multiplebarcodes=true" 
                      alt="Gusto Studio Barcode"
                      className="qr-code" 
                    />
                    <p className="barcode-text">Scan for Studio Menu</p>
                 </div>
                 
                 <p style={{marginTop: '15px', fontSize: '8px', opacity: 0.5}}>© GUSTO PIZZERIA STUDIO</p>
               </div>

               {/* --- On-Screen Beauty UI (Hidden on Print) --- */}
               <div className="no-print w-full flex flex-col items-center">
                 <div className="w-full flex justify-between items-center mb-6 md:mb-8">
                   <div className="flex flex-col">
                      <span className="font-black uppercase tracking-[0.4em] text-[8px] md:text-[10px] text-[#E05A38]">Order Confirmed</span>
                      <h2 className="font-display text-3xl md:text-6xl text-[#1A3C28] font-black uppercase leading-none tracking-tighter">
                        GUS<span className="text-[#E05A38]">TO</span> RECEIPT
                      </h2>
                   </div>
                   <button 
                     onClick={() => setOrderStatus('minimized')}
                     className="p-3 md:p-4 bg-[#1A3C28] text-white rounded-full hover:scale-110 active:scale-90 transition-transform shadow-lg"
                   >
                     <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M18 6L6 18M6 6l12 12"/></svg>
                   </button>
                 </div>

                 <div className="w-full bg-white rounded-[1.5rem] md:rounded-[2.5rem] p-5 md:p-8 shadow-xl border border-[#1A3C28]/10 mb-6 md:mb-8 relative overflow-hidden flex flex-col items-center justify-center min-h-[180px] md:min-h-[280px]">
                   <div className="absolute top-4 right-6 text-right">
                      <p className="font-black text-[7px] md:text-[9px] uppercase tracking-widest Status">Status</p>
                      <p className="font-display font-black text-[#1A3C28] text-sm md:text-xl uppercase italic leading-none mt-1">
                        {isDelivered ? "Delivered" : (isDelivering ? "On the way" : "In the oven")}
                      </p>
                   </div>

                   <div className="relative w-full flex justify-center mt-8 md:mt-12 py-2 md:py-6 scale-[0.8] md:scale-100">
                      {isDelivered ? (
                        <div className="relative w-48 h-24 flex items-center justify-center animate-in fade-in zoom-in duration-700">
                          <svg width="120" height="120" viewBox="0 0 100 100" className="overflow-visible">
                            <path d="M20,90 L20,45 L50,20 L80,45 L80,90 Z" fill="#1A3C28" />
                            <path d="M15,48 L50,15 L85,48" fill="none" stroke="#E05A38" strokeWidth="6" strokeLinecap="round" />
                            <g transform="translate(35, 45) scale(0.3)">
                              {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                                <g key={i} transform={`rotate(${angle} 50 50)`}>
                                  <path d="M50,50 L50,10 L75,25 Z" fill={i % 2 === 0 ? "#E05A38" : "#F2E9D8"} />
                                </g>
                              ))}
                              <circle cx="50" cy="50" r="8" fill="#F2E9D8" />
                            </g>
                            <circle cx="85" cy="75" r="18" fill="#E05A38" className="animate-pulse shadow-xl" />
                            <path d="M78,75 L83,80 L92,70" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      ) : !isDelivering ? (
                        <svg width="120" height="120" viewBox="0 0 100 100" className="animate-in fade-in zoom-in duration-500 overflow-visible">
                          <path d="M10,80 Q10,20 50,20 Q90,20 90,80" fill="#4A4A4A" stroke="#1A3C28" strokeWidth="3" />
                          <rect x="5" y="80" width="90" height="10" rx="2" fill="#333" />
                          <path d="M25,80 Q25,45 50,45 Q75,45 75,80" fill="#1A1A1A" />
                          <path d="M30,80 Q30,55 50,55 Q70,55 70,80" fill="#E05A38" opacity="0.6">
                            <animate attributeName="opacity" values="0.4;0.9;0.4" dur="2s" repeatCount="indefinite" />
                          </path>
                          <g>
                            <animateTransform attributeName="transform" type="translate" values="0,0; -5,0; 0,0" dur="3s" repeatCount="indefinite" />
                            <rect x="35" y="70" width="40" height="2" fill="#D2B48C" />
                            <circle cx="45" cy="71" r="12" fill="#FFD700" stroke="#E05A38" strokeWidth="1" />
                            <circle cx="42" cy="68" r="1.5" fill="#E05A38" />
                            <circle cx="48" cy="73" r="1.5" fill="#E05A38" />
                            <circle cx="40" cy="75" r="1.5" fill="#E05A38" />
                          </g>
                        </svg>
                      ) : (
                        <div className="relative w-48 h-24 flex items-center justify-center animate-in slide-in-from-left duration-1000">
                          <svg width="100" height="80" viewBox="0 0 100 80" className="animate-bounce overflow-visible">
                            <path d="M20,60 Q20,30 40,30 L70,30 Q85,30 85,60 Z" fill="#E05A38" stroke="#1A3C28" strokeWidth="2" />
                            <circle cx="30" cy="65" r="10" fill="#333" stroke="#F2E9D8" strokeWidth="2" />
                            <circle cx="75" cy="65" r="10" fill="#333" stroke="#F2E9D8" strokeWidth="2" />
                            <path d="M35,30 L30,15 L25,18" fill="none" stroke="#1A3C28" strokeWidth="3" strokeLinecap="round" />
                            <circle cx="50" cy="20" r="8" fill="#E8BEAC" />
                            <path d="M42,20 A8,8 0 0 1 58,20" fill="#1A3C28" />
                            <rect x="65" y="15" width="20" height="15" fill="#F2E9D8" stroke="#1A3C28" strokeWidth="1" />
                            <text x="70" y="27" fill="#E05A38" fontSize="10" fontWeight="900" fontFamily="Syne">G</text>
                            <g stroke="#1A3C28" strokeWidth="2" opacity="0.3">
                              <line x1="5" y1="35" x2="-10" y2="35">
                                <animate attributeName="x1" values="5;-10;5" dur="0.4s" repeatCount="indefinite" />
                                <animate attributeName="x2" values="-10;-25;-10" dur="0.4s" repeatCount="indefinite" />
                              </line>
                            </g>
                          </svg>
                        </div>
                      )}
                   </div>

                   <div className="w-full max-w-xs mt-4">
                      <div className="h-2 md:h-3 w-full bg-[#1A3C28]/5 rounded-full overflow-hidden border border-[#1A3C28]/20">
                         <div 
                           className="h-full bg-[#E05A38] transition-all duration-1000 ease-linear" 
                           style={{ width: `${progressPercent}%` }}
                         ></div>
                      </div>
                   </div>
                 </div>

                 <div className="w-full grid md:grid-cols-2 gap-4 md:gap-6 mb-8">
                    <div className="bg-[#1A3C28] text-[#F2E9D8] rounded-[1.5rem] md:rounded-[2rem] p-5 md:p-8 flex flex-col gap-4 shadow-lg">
                      <h4 className="font-black uppercase tracking-[0.4em] text-[8px] opacity-40">Your Selection</h4>
                      <div className="space-y-3 md:space-y-4 max-h-[150px] md:max-h-[220px] overflow-y-auto scrollbar-hide">
                        {orderedItems.map(item => {
                          const unitPrice = getItemUnitPrice(item);
                          return (
                            <div key={item.id} className="flex justify-between items-center group">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 md:w-14 md:h-14 rounded-lg md:rounded-xl overflow-hidden border border-white/10 group-hover:border-[#E05A38] transition-all flex-shrink-0">
                                  <img src={item.image} className="w-full h-full object-cover" alt={item.name} />
                                </div>
                                <div className="min-w-0">
                                   <p className="font-display font-black text-xs md:text-base uppercase leading-none tracking-tight truncate">{item.name}</p>
                                   <p className="font-black text-[8px] md:text-[9px] uppercase text-[#E05A38] mt-1">QTY: {item.quantity}</p>
                                   {item.customization?.crust && item.customization.crust !== '72-Hour Standard Sourdough' && (
                                     <p className="text-[8px] text-white/70 font-semibold mt-0.5">• Crust: {item.customization.crust.split('(')[0].trim()}</p>
                                   )}
                                   {item.customization?.extraToppings && item.customization.extraToppings.length > 0 && (
                                     <p className="text-[8px] text-[#E05A38] font-bold">
                                       • +{item.customization.extraToppings.map(t => t.name).join(', ')}
                                     </p>
                                   )}
                                </div>
                              </div>
                              <p className="font-display text-base md:text-xl font-black">${(unitPrice * item.quantity).toFixed(2)}</p>
                            </div>
                          );
                        })}
                      </div>
                      <div className="pt-4 border-t border-white/10 flex flex-col gap-2">
                         <div className="flex justify-between items-end">
                            <p className="font-black uppercase tracking-[0.3em] text-[8px] opacity-40">Total</p>
                            <p className="font-display text-3xl md:text-5xl font-black tracking-tighter">${orderedTotal}</p>
                         </div>
                         <div className="flex justify-between items-center">
                            <p className="font-black uppercase tracking-[0.3em] text-[8px] opacity-40">Payment Status</p>
                            <p className={`font-black uppercase text-[9px] px-2 py-0.5 rounded ${persistedPaymentMethod === 'digital' ? 'bg-[#E05A38] text-white' : 'bg-white/10 text-white/40'}`}>
                               {persistedPaymentMethod === 'digital' ? 'PAID / ZERO BALANCE' : 'CASH ON DELIVERY'}
                            </p>
                         </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4 py-2">
                      <div className="space-y-0.5">
                         <p className="font-black text-[8px] md:text-[9px] uppercase tracking-widest opacity-30">Status</p>
                         <p className="font-display text-3xl md:text-5xl font-black text-[#1A3C28] uppercase italic leading-none">
                           {isDelivered ? "ARRIVED" : "~ 10 MINS"}
                         </p>
                      </div>
                      <div className="bg-white/40 border border-[#1A3C28]/10 rounded-[1.25rem] p-4 md:p-6 shadow-sm backdrop-blur-sm">
                         <p className="font-black text-[8px] md:text-[9px] uppercase tracking-widest opacity-40 mb-1.5">Note</p>
                         <p className="text-[10px] md:text-xs font-bold leading-relaxed text-[#1A3C28]/70 italic">
                           {isDelivered 
                             ? '"Your order has arrived. Enjoy."' 
                             : (persistedPaymentMethod === 'digital' 
                                 ? '"Order paid. Your pizza is on its way."' 
                                 : '"Your pizza is on its way."')}
                         </p>
                      </div>
                    </div>
                 </div>

                 <div className="flex flex-wrap gap-3 md:gap-4 w-full justify-center mb-10">
                   <button 
                     onClick={() => window.print()}
                     className="bg-[#E05A38] text-white px-6 md:px-10 py-3 md:py-4 rounded-full font-black uppercase tracking-widest text-[9px] md:text-[10px] hover:scale-105 active:scale-95 transition-all shadow-xl flex items-center justify-center gap-2"
                   >
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2m-2-4H8v8h8v-8z"/></svg>
                     Print Receipt
                   </button>
                   <button 
                     onClick={handleShare}
                     className="bg-[#1A3C28] text-[#F2E9D8] px-6 md:px-10 py-3 md:py-4 rounded-full font-black uppercase tracking-widest text-[9px] md:text-[10px] hover:scale-105 active:scale-95 transition-all shadow-xl flex items-center justify-center gap-2 border border-white/10"
                   >
                     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8m-4-6l-4-4-4 4m4-4v13"/></svg>
                     Share Receipt
                   </button>
                   <button 
                     onClick={() => setOrderStatus('minimized')}
                     className="bg-[#1A3C28] text-white px-6 md:px-10 py-3 md:py-4 rounded-full font-black uppercase tracking-widest text-[9px] md:text-[10px] hover:scale-105 active:scale-95 transition-all shadow-xl"
                   >
                     Back to Studio
                   </button>
                 </div>
               </div>
             </div>
           ) : (
             <>
               <div className="flex items-center gap-3 md:gap-5">
                 <span className="text-lg md:text-2xl animate-pulse drop-shadow-[0_0_8px_rgba(224,90,56,0.6)]">
                    {isDelivered ? "🏠" : (isDelivering ? "🛵" : "🍕")}
                 </span>
                 <div className="text-left">
                   <p className="font-display font-black text-[#F2E9D8] text-[9px] md:text-sm uppercase tracking-tighter leading-none">
                     LIVE: <span className="text-[#E05A38] animate-pulse">{isDelivered ? "ARRIVED" : (isDelivering ? "SHIPPING" : "BAKING")}</span>
                   </p>
                   <p className="font-black text-[#F2E9D8]/40 uppercase tracking-[0.2em] text-[7px] md:text-8px mt-0.5 whitespace-nowrap">
                     {isDelivered ? "DELIVERED" : `~ ${Math.max(1, 10 - Math.floor(elapsedMinutes))}M LEFT`}
                   </p>
                 </div>
               </div>
               <button 
                 onClick={() => setOrderStatus('success')}
                 className="bg-[#E05A38] text-white px-3 md:px-5 py-1.5 md:py-2 rounded-full font-black uppercase text-[7px] md:text-[9px] tracking-widest shadow-lg active:scale-90 transition-transform flex items-center gap-1"
               >
                 <span>RECEIPT</span>
                 <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M5 12h14m-7-7l7 7-7 7"/></svg>
               </button>
             </>
           )}
        </div>
      )}

      {/* Premium Sticky Header */}
      <header 
        className={`fixed top-0 left-0 w-full z-[100] header-transition px-4 md:px-8 py-2 flex justify-between items-center ${
          scrolled 
          ? 'bg-[#F2E9D8]/95 backdrop-blur-xl border-b border-black/5 py-1.5 shadow-sm' 
          : 'bg-transparent'
        } ${orderStatus === 'minimized' ? bannerShift : ''}`}
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 md:w-12 md:h-12 flex items-center justify-center relative will-change-transform">
            <div className={`absolute inset-0 rounded-full border transition-colors duration-300 ${scrolled ? 'border-[#1A3C28]/10' : 'border-white/20'} scale-110`}></div>
            <div className="w-full h-full relative animate-logo-rotate flex items-center justify-center">
              <svg viewBox="0 0 100 100" className="w-full h-full overflow-visible">
                <defs>
                  <linearGradient id="sliceGlow" x1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#E05A38" />
                    <stop offset="100%" stopColor="#8A2B12" />
                  </linearGradient>
                </defs>
                {[0, 60, 120, 180, 240, 300].map((angle, i) => (
                  <g key={i} transform={`rotate(${angle} 50 50)`}>
                    <path 
                      d="M50,50 L50,10 L75,25 Z" 
                      fill={i % 2 === 0 ? "url(#sliceGlow)" : (scrolled ? "#1A3C28" : "#F2E9D8")}
                      className="drop-shadow-sm transition-colors duration-300"
                    />
                  </g>
                ))}
                <circle cx="50" cy="50" r="8" fill="#F2E9D8" />
                <circle cx="50" cy="50" r="4" fill="#1A3C28" />
              </svg>
            </div>
          </div>
          <span className="font-display font-black text-lg md:text-xl tracking-tighter uppercase transition-colors duration-300">
            <span className={scrolled ? "text-[#1A3C28]" : "text-[#F2E9D8]"}>GUS</span><span className="text-[#E05A38]">TO</span>
          </span>
        </div>
        
        <button 
          onClick={() => setIsCartOpen(true)}
          className="bg-[#E05A38] text-white px-4 md:px-6 py-1.5 md:py-2 rounded-full font-black uppercase text-[9px] md:text-[11px] tracking-widest shadow-lg active:scale-95 transition-all flex items-center gap-2"
        >
          Order {totalItems > 0 && <span className="bg-white text-[#E05A38] w-4 h-4 md:w-5 md:h-5 flex items-center justify-center rounded-full text-[8px] md:text-[10px] font-black">{totalItems}</span>}
        </button>
      </header>

      {/* Cart Sidebar */}
      <div 
        className={`fixed inset-0 z-[200] ${isInitialized ? 'transition-opacity duration-500' : 'opacity-0'} ${isCartOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      >
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setIsCartOpen(false)}></div>
        <div className={`absolute top-0 right-0 h-full w-full max-w-md bg-[#1A3C28] shadow-2xl transition-transform duration-500 transform ${isCartOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>
          <div className="p-6 md:p-8 flex justify-between items-center border-b border-white/10">
            <h2 className="font-display text-2xl md:text-3xl text-[#F2E9D8] font-black uppercase tracking-tighter">Your Bag</h2>
            <button onClick={() => setIsCartOpen(false)} className="text-[#F2E9D8] hover:text-[#E05A38] transition-colors p-2">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </button>
          </div>

          <div ref={cartItemsRef} className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6 scrollbar-hide">
            {cart.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center space-y-4 opacity-40">
                <div className="text-6xl animate-bounce">🍕</div>
                <p className="font-black uppercase tracking-widest text-[10px] text-[#F2E9D8]">The oven is empty.</p>
              </div>
            ) : (
              cart.map(item => {
                const itemUnitPrice = getItemUnitPrice(item);
                const isCustomizing = openCustomizingId === item.id;
                return (
                  <div key={item.id} className="cart-item-anim border-b border-white/5 pb-4 space-y-3 group">
                    <div className="flex gap-4 items-center">
                      <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden flex-shrink-0 border-2 border-white/10 group-hover:border-[#E05A38]/40 transition-colors">
                        <img src={item.image} className="w-full h-full object-cover" alt={item.name} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-[#F2E9D8] font-black uppercase text-xs tracking-widest truncate">{item.name}</h3>
                        <p className="text-[#E05A38] font-black text-xs mt-0.5">${(itemUnitPrice * item.quantity).toFixed(2)}</p>
                        
                        {/* Customization Badges */}
                        <div className="flex flex-wrap gap-1 mt-1">
                          {item.customization?.crust && item.customization.crust !== '72-Hour Standard Sourdough' && (
                            <span className="text-[8px] bg-white/10 text-[#F2E9D8]/80 px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                              {item.customization.crust.split('(')[0].trim()}
                            </span>
                          )}
                          {item.customization?.extraToppings?.map(t => (
                            <span key={t.name} className="text-[8px] bg-[#E05A38]/20 text-[#E05A38] px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                              +{t.name}
                            </span>
                          ))}
                        </div>

                        <div className="flex items-center justify-between gap-3 mt-2">
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQuantity(item.id, -1)} className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center text-[#F2E9D8] hover:bg-white/10 text-xs font-black">-</button>
                            <span className="text-[#F2E9D8] font-black text-xs w-4 text-center">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.id, 1)} className="w-6 h-6 rounded-full border border-white/20 flex items-center justify-center text-[#F2E9D8] hover:bg-white/10 text-xs font-black">+</button>
                          </div>
                          
                          <button
                            onClick={() => setOpenCustomizingId(isCustomizing ? null : item.id)}
                            className="text-[9px] font-black uppercase tracking-wider text-[#E05A38] underline hover:text-white transition-colors"
                          >
                            {isCustomizing ? 'Done' : 'Customize ✏️'}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Customization Panel */}
                    {isCustomizing && (
                      <div className="bg-[#143020] p-4 rounded-2xl space-y-3.5 border-2 border-[#E05A38]/30 shadow-2xl text-xs text-[#F2E9D8] animate-in fade-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center border-b border-white/10 pb-2">
                          <span className="font-display font-black uppercase text-xs text-[#E05A38]">
                            Customization Options
                          </span>
                          <button
                            type="button"
                            onClick={() => setOpenCustomizingId(null)}
                            className="bg-[#E05A38] text-white px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider hover:scale-105 transition-transform"
                          >
                            Done ✓
                          </button>
                        </div>

                        <div>
                          <label className="font-black uppercase text-[9px] tracking-[0.2em] text-[#F2E9D8]/60 block mb-1.5">
                            Crust Style
                          </label>
                          <select 
                            value={item.customization?.crust || '72-Hour Standard Sourdough'}
                            onChange={e => setItemCrust(item.id, e.target.value)}
                            className="w-full bg-[#1A3C28] border border-white/20 rounded-xl p-2 text-[10px] font-bold text-white focus:outline-none focus:border-[#E05A38] cursor-pointer"
                          >
                            {AVAILABLE_CRUSTS.map(crust => (
                              <option key={crust} value={crust}>{crust}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="font-black uppercase text-[9px] tracking-[0.2em] text-[#F2E9D8]/60 block mb-1.5">
                            Extra Artisanal Toppings
                          </label>
                          <div className="grid grid-cols-2 gap-1.5">
                            {AVAILABLE_TOPPINGS.map(topping => {
                              const isSelected = item.customization?.extraToppings?.some(t => t.name === topping.name);
                              return (
                                <button
                                  key={topping.name}
                                  type="button"
                                  onClick={() => toggleItemTopping(item.id, topping)}
                                  className={`text-[9px] font-black p-2 rounded-xl border text-left transition-all flex justify-between items-center ${
                                    isSelected 
                                      ? 'bg-[#E05A38] text-white border-[#E05A38] shadow-md' 
                                      : 'bg-white/5 border-white/10 text-[#F2E9D8]/80 hover:border-white/30'
                                  }`}
                                >
                                  <span className="truncate flex items-center gap-1">
                                    {isSelected && <span>✓</span>}
                                    <span>{topping.name}</span>
                                  </span>
                                  <span className="ml-1 text-[8px] opacity-80">+${topping.price.toFixed(2)}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Notes Section within Cart */}
          {cart.length > 0 && (
            <div className="px-6 md:px-8 py-3 border-t border-white/5 space-y-3">
               <div>
                 <label className="font-black uppercase text-[8px] tracking-[0.3em] text-[#F2E9D8]/40 mb-1 block">Order Notes</label>
                 <textarea 
                   value={orderNotes}
                   onChange={(e) => setOrderNotes(e.target.value)}
                   placeholder="Any special requests for the chefs?"
                   className="w-full bg-white/5 border border-white/10 rounded-xl p-2.5 text-white text-[10px] font-bold focus:outline-none focus:border-[#E05A38] transition-colors placeholder:opacity-20 resize-none"
                   rows={2}
                 />
               </div>
            </div>
          )}

          <div className="p-6 md:p-8 bg-black/20 border-t border-white/10 space-y-4">
            <div className="flex justify-between items-end">
              <p className="font-black uppercase text-[10px] tracking-[0.3em] text-[#F2E9D8]/40">Subtotal</p>
              <p className="text-[#F2E9D8] text-3xl md:text-4xl font-black font-display tracking-tighter">${totalPrice.toFixed(2)}</p>
            </div>
            <button 
              disabled={cart.length === 0}
              onClick={() => {
                setIsCartOpen(false);
                setIsCheckoutOpen(true);
              }}
              className="w-full bg-[#E05A38] text-white py-4 md:py-5 rounded-full font-black uppercase tracking-widest text-xs hover:scale-[1.02] active:scale-95 transition-all shadow-xl disabled:opacity-30 disabled:pointer-events-none"
            >
              Checkout Now
            </button>
          </div>
        </div>
      </div>

      {/* Checkout Overlay */}
      <CheckoutOverlay 
        isOpen={isCheckoutOpen} 
        onClose={() => setIsCheckoutOpen(false)}
        cartItems={cart}
        totalPrice={totalPrice}
        onOrderSuccess={onOrderSuccess}
        orderNotes={orderNotes}
      />

      <main className={`w-full transition-all duration-700 ${orderStatus === 'minimized' ? 'mt-[50px] md:mt-[60px]' : ''}`}>
        <Hero />
        {/*
          Premium editorial divider — replaces the oversized "FORGED IN FIRE" headline.
          Slim, restrained, three-column manifesto with a thin sienna rule above.
          Reads like a Michelin-guide restaurant credit line, not a tagline.
        */}
        <section className="bg-[#1A3C28] py-[6vh] md:py-[7vh] px-6 md:px-12 flex flex-col items-center justify-center text-center overflow-hidden border-b border-white/5">
          <div className="w-px h-[3vh] bg-[#E05A38] opacity-50 mb-[3vh]"></div>
          <div className="max-w-5xl mx-auto">
            <p className="font-display text-[#F2E9D8] uppercase tracking-tight leading-[1.05] manifest-headline">
              Fire, flour, and time.
            </p>
            <div className="grid grid-cols-3 gap-4 md:gap-12 mt-[4vh] max-w-3xl mx-auto">
              <div className="flex flex-col items-center">
                <span className="font-display text-[#E05A38] font-black manifest-stat">72hr</span>
                <span className="font-black uppercase tracking-[0.3em] text-[#F2E9D8]/40 manifest-stat-label mt-1">Cold Ferment</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="font-display text-[#E05A38] font-black manifest-stat">900°F</span>
                <span className="font-black uppercase tracking-[0.3em] text-[#F2E9D8]/40 manifest-stat-label mt-1">Stone Hearth</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="font-display text-[#E05A38] font-black manifest-stat">FL</span>
                <span className="font-black uppercase tracking-[0.3em] text-[#F2E9D8]/40 manifest-stat-label mt-1">Sourced Local</span>
              </div>
            </div>
          </div>
        </section>
        <GustoRotator onAddToCart={addToCart} />
        <section className="bg-[#F2E9D8]">
          <MenuGrid />
        </section>
        <TickerMarquee />
        <HandStory />

        {/* Order History Persistent Footer Section */}
        {orderHistory.length > 0 && (
          <section className="bg-[#143020] text-[#F2E9D8] py-12 px-6 md:px-12 border-t border-white/5">
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="flex justify-between items-center border-b border-white/10 pb-4">
                <div>
                  <h3 className="font-display text-2xl md:text-3xl font-black uppercase tracking-tighter text-[#F2E9D8]">
                    Order <span className="text-[#E05A38]">History</span>
                  </h3>
                  <p className="text-xs text-[#F2E9D8]/60 font-bold mt-1">Your past orders saved on this device for 1-click reordering.</p>
                </div>
                <button
                  onClick={() => setIsOrderHistoryOpen(!isOrderHistoryOpen)}
                  className="bg-[#E05A38]/20 text-[#E05A38] hover:bg-[#E05A38] hover:text-white px-4 py-2 rounded-full font-black text-[10px] uppercase tracking-widest transition-all"
                >
                  {isOrderHistoryOpen ? 'Collapse' : `View ${orderHistory.length} Past Orders`}
                </button>
              </div>

              {isOrderHistoryOpen && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-in fade-in duration-300">
                  {orderHistory.map(pastOrder => (
                    <div key={pastOrder.id} className="bg-[#1A3C28] p-5 rounded-2xl border border-white/10 flex flex-col justify-between gap-4 shadow-lg">
                      <div className="space-y-3">
                        <div className="flex justify-between items-center border-b border-white/5 pb-2">
                          <span className="font-display font-black text-sm text-[#E05A38]">{pastOrder.id}</span>
                          <span className="text-[10px] font-bold text-[#F2E9D8]/50">{pastOrder.date}</span>
                        </div>
                        
                        <div className="space-y-1.5 text-xs">
                          {pastOrder.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-start text-[#F2E9D8]/80 font-bold">
                              <div>
                        <span>{item.quantity}x {item.name}</span>
                        {item.customization?.crust && item.customization.crust !== '72-Hour Standard Sourdough' && (
                          <p style={{ fontSize: '8px', opacity: 0.8 }}>• {item.customization.crust.split('(')[0].trim()}</p>
                        )}
                        {item.customization?.extraToppings && item.customization.extraToppings.length > 0 && (
                          <p style={{ fontSize: '8px', opacity: 0.8 }}>• +{item.customization.extraToppings.map(t => t.name).join(', ')}</p>
                        )}
                      </div>
                              <span className="text-[#E05A38]">${(getItemUnitPrice(item) * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="pt-3 border-t border-white/10 flex justify-between items-center">
                        <div>
                          <p className="text-[8px] font-black uppercase tracking-widest text-[#F2E9D8]/40">Total Paid</p>
                          <p className="font-display text-lg font-black text-[#F2E9D8]">${pastOrder.total.toFixed(2)}</p>
                        </div>
                        <button
                          onClick={() => {
                            setCart(pastOrder.items);
                            setIsCartOpen(true);
                          }}
                          className="bg-[#E05A38] text-white px-4 py-2 rounded-full font-black text-[9px] uppercase tracking-widest hover:scale-105 active:scale-95 transition-all shadow-md"
                        >
                          Reorder All 🍕
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        <footer className="bg-[#1A3C28] text-[#F2E9D8] pt-20 pb-12 px-6 md:px-12 overflow-hidden border-t border-white/5">
          <div className="max-w-7xl mx-auto flex flex-col gap-12">
            
            {/* Top Brand Banner */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b border-white/10 pb-10">
              <div>
                <h3 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter text-[#F2E9D8]">
                  GUS<span className="text-[#E05A38]">TO</span> STUDIO
                </h3>
                <p className="font-bold text-xs md:text-sm text-[#F2E9D8]/60 mt-2 max-w-md">
                  Sculpted by heat. Defined by gravity. Hand-built 72-hour fermented sourdough pizza in Tallahassee.
                </p>
              </div>
              <button
                onClick={() => setIsCartOpen(true)}
                className="bg-[#E05A38] text-white px-8 py-3.5 rounded-full font-black uppercase text-xs tracking-widest hover:scale-105 active:scale-95 transition-all shadow-xl"
              >
                Order Online Now
              </button>
            </div>

            {/* Grid options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
              
              {/* Working Navigation */}
              <div className="space-y-4">
                <p className="font-black text-[10px] uppercase tracking-[0.3em] text-[#E05A38]">Explore Menu</p>
                <ul className="space-y-2.5 font-bold text-sm">
                  <li>
                    <button 
                      onClick={() => {
                        setIsCartOpen(true);
                      }} 
                      className="hover:text-[#E05A38] transition-colors flex items-center gap-2"
                    >
                      <span className="text-xs">🍕</span> Start Order
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => {
                        window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
                      }} 
                      className="hover:text-[#E05A38] transition-colors flex items-center gap-2"
                    >
                      <span className="text-xs">🔥</span> Rotating Pizzas
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => {
                        const el = document.querySelector('.section-header');
                        el?.scrollIntoView({ behavior: 'smooth' });
                      }} 
                      className="hover:text-[#E05A38] transition-colors flex items-center gap-2"
                    >
                      <span className="text-xs">✨</span> Local Icons
                    </button>
                  </li>
                  {orderHistory.length > 0 && (
                    <li>
                      <button 
                        onClick={() => setIsOrderHistoryOpen(true)} 
                        className="text-[#E05A38] hover:underline font-black flex items-center gap-2"
                      >
                        <span className="text-xs">📜</span> Past Orders ({orderHistory.length})
                      </button>
                    </li>
                  )}
                  {orderStatus !== 'idle' && (
                    <li>
                      <button 
                        onClick={() => setOrderStatus('success')} 
                        className="text-[#E05A38] hover:underline font-black flex items-center gap-2"
                      >
                        <span className="text-xs">🧾</span> View Active Receipt
                      </button>
                    </li>
                  )}
                </ul>
              </div>

              {/* Location & Contact */}
              <div className="space-y-4">
                <p className="font-black text-[10px] uppercase tracking-[0.3em] text-[#E05A38]">Location & Order</p>
                <p className="font-bold text-sm leading-relaxed">
                  102 S Bronough St,<br />
                  Tallahassee, FL 32301
                </p>
                <p className="font-bold text-xs text-[#F2E9D8]/70">
                  Phone: <a href="tel:8504878600" className="underline hover:text-[#E05A38]">(850) GUSTO-PIZZA</a>
                </p>
              </div>

              {/* Operating Hours */}
              <div className="space-y-4">
                <p className="font-black text-[10px] uppercase tracking-[0.3em] text-[#E05A38]">Studio Hours</p>
                <ul className="space-y-1.5 font-bold text-xs text-[#F2E9D8]/80">
                  <li className="flex justify-between">
                    <span>Mon - Thu:</span>
                    <span className="text-[#F2E9D8]">12 PM - 10 PM</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Fri - Sat:</span>
                    <span className="text-[#F2E9D8]">12 PM - 11 PM</span>
                  </li>
                  <li className="flex justify-between">
                    <span>Sunday:</span>
                    <span className="text-[#F2E9D8]">12 PM - 9 PM</span>
                  </li>
                </ul>
              </div>

              {/* Social & Connect */}
              <div className="space-y-4">
                <p className="font-black text-[10px] uppercase tracking-[0.3em] text-[#E05A38]">Connect</p>
                <div className="flex flex-col gap-2.5 font-bold text-sm">
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#E05A38] transition-colors">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line></svg>
                    Instagram
                  </a>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#E05A38] transition-colors">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"></path></svg>
                    Facebook
                  </a>
                  <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-[#E05A38] transition-colors">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"></path></svg>
                    Twitter / X
                  </a>
                </div>
              </div>

            </div>

            {/* Bottom Credits Bar */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-6 pt-8 border-t border-white/10 text-[10px] uppercase font-black tracking-[0.3em]">
              <p className="opacity-50">©2025 GUSTO PIZZERIA STUDIO. ALL RIGHTS RESERVED.</p>
              
              <div className="flex items-center">
                 <a 
                   href="https://batchsystems.space" 
                   target="_blank" 
                   rel="noopener noreferrer"
                   className="bg-black border border-[#E05A38] px-5 py-2 rounded-full text-[#E05A38] text-[9px] md:text-[10px] font-black uppercase tracking-widest hover:bg-[#E05A38] hover:text-[#F2E9D8] transition-all cursor-pointer shadow-2xl flex items-center gap-2"
                 >
                   Developed by Batch Systems
                   <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/></svg>
                 </a>
              </div>
              
              <p className="opacity-50">Tallahassee, Florida, USA</p>
            </div>
          </div>
        </footer>
      </main>

      {/* PWA Install Banner — compact, zero negative space gap */}
      {showInstallBanner && (
        <div
          ref={installBannerRef}
          className="fixed bottom-4 right-4 z-[999] max-w-[calc(100vw-2rem)] pointer-events-auto no-print"
        >
          <div
            className="bg-[#1A3C28] text-[#F2E9D8] rounded-2xl p-2.5 px-3.5 shadow-[0_10px_30px_rgba(0,0,0,0.45)] border border-[#E05A38]/40 flex items-center justify-between gap-3 backdrop-blur-2xl relative overflow-hidden group"
          >
            {/* Subtle Pulse Animation Layer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#E05A38]/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

            <div className="flex items-center gap-2 min-w-0 relative z-10">
              <span className="text-sm">🍕</span>
              <div className="leading-tight">
                <p className="font-display font-black text-xs uppercase tracking-tight text-[#F2E9D8]">GUSTO</p>
                <p className="font-black text-[9px] uppercase tracking-wider text-[#E05A38]">
                  Add to Home to order quickly
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 relative z-10 flex-shrink-0">
              {!isIOS && (
                <button
                  onClick={handleInstallClick}
                  className="bg-[#E05A38] text-white px-3 py-1.5 rounded-full font-black uppercase text-[8px] tracking-widest hover:scale-105 active:scale-95 transition-all shadow-[0_0_10px_rgba(224,90,56,0.5)]"
                >
                  Install
                </button>
              )}
              <button
                onClick={() => setShowInstallBanner(false)}
                className="p-1 text-white/40 hover:text-white transition-colors flex-shrink-0"
                aria-label="Dismiss install banner"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><path d="M18 6L6 18M6 6l12 12"/></svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default App;